import { useResolvedDesignSystemComponent } from 'ext-ui/design-system/DesignSystemChannel'
import GeneratedLatestButton, {
  ButtonProps as GeneratedButtonProps,
} from 'ext-ui/design-system/generated/latest/components/Button/Button'
import GeneratedLiveButton from 'ext-ui/design-system/generated/live/components/Button/Button'
import React, { forwardRef, ReactElement } from 'react'

import ButtonLegacy, { ButtonProps } from './ButtonLegacy'

type PublicButtonVariant = NonNullable<ButtonProps['variant']>
type GeneratedButtonVariant = NonNullable<GeneratedButtonProps['variant']>

const generatedVariantByPublicVariant: Partial<
  Record<PublicButtonVariant, GeneratedButtonVariant>
> = {
  primary: 'default',
  secondary: 'secondary',
  severe: 'severe',
  'severe-secondary': 'severeSecondary',
  'no-shell': 'noShell',
  text: 'inline',
  'no-style': 'inlineMuted',
}

function generatedVariantFor(
  variant: ButtonProps['variant'],
): GeneratedButtonVariant | null {
  return generatedVariantByPublicVariant[variant ?? 'primary'] ?? null
}

function fallbackChannelMarker(channel: 'live' | 'latest'): string {
  return channel === 'latest' ? 'latest-live-fallback' : 'live-legacy-fallback'
}

function renderDesignSystemButton(
  Component: typeof GeneratedLiveButton,
  channel: 'live' | 'latest',
  props: ButtonProps,
  forwardedRef: React.ForwardedRef<HTMLButtonElement>,
): ReactElement {
  const generatedVariant = generatedVariantFor(props.variant)
  const shouldUseLegacyFallback =
    !generatedVariant ||
    Boolean(
      forwardedRef ||
        (props.disabled && props.disabledTooltip) ||
        props.loading,
    )

  if (shouldUseLegacyFallback) {
    return (
      <ButtonLegacy
        {...props}
        ref={forwardedRef}
        data-design-system-channel={fallbackChannelMarker(channel)}
      />
    )
  }

  const {
    'data-testid': dataTestId,
    className,
    disabledTooltip: _disabledTooltip,
    dropShadow = true,
    loading,
    testId,
    variant,
    ...rest
  } = props
  const publicVariant = variant ?? 'primary'

  return (
    <Component
      {...rest}
      buttonStyle={dropShadow ? 'dropShadow' : undefined}
      className={[className, publicVariant].filter(Boolean).join(' ')}
      data-design-system-channel={channel}
      data-testid={dataTestId ?? testId ?? 'button'}
      isLoading={loading}
      type={rest.type ?? 'button'}
      variant={generatedVariant}
    />
  )
}

const LiveButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function LiveDesignSystemButton(props, forwardedRef): ReactElement {
    return renderDesignSystemButton(
      GeneratedLiveButton,
      'live',
      props,
      forwardedRef,
    )
  },
)

const LatestButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function LatestDesignSystemButton(props, forwardedRef): ReactElement {
    return renderDesignSystemButton(
      GeneratedLatestButton,
      'latest',
      props,
      forwardedRef,
    )
  },
)

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, forwardedRef): ReactElement {
    const ResolvedButton = useResolvedDesignSystemComponent(
      LiveButton,
      LatestButton,
    )

    return <ResolvedButton {...props} ref={forwardedRef} />
  },
)

export default Button
export type { ButtonProps }
